import { ReactTFC } from "../../../../../utils/fp/missing-ts-parts";
import { PurchaseConfirmationPageTranslation, type TranslatedValue } from "@swo/storefront";
import { PaymentStepCtxReq } from "../../use-payment-step";
import { CustomerOrderPaymentStatusResponse } from "@swo/storefront";
import { useStore } from "../../../../../store/hooks/use-store";
import { Navigate, useLocation } from "react-router";
import React, { useState } from "react";
import { useAbortableFetched, useStale } from "../../../../../hooks";
import { ErrorPage } from "../../error-page";
import { PurchaseConfirmationPageSuccessScreen } from "../../purchase-confirmation-page-success-screen";
import { Wrapper } from "../../shared-components/wrapper";
import cn from "classnames";
import { ContentContainer } from "../../../../../components/_shared-components/arrangement/Container";
import { PurchaseConfirmationPageErrorScreen } from "../payment-error-page/purchase-confirmation-page-error-screen";
import { css } from "@emotion/react";
import { Payment3DSChallenge } from "../../payment-methods/credit-card/payment-3ds-challenge";
import { PaypalPayButton } from "../../payment-methods/paypal";
import { MakeSwishPayment } from "../../payment-methods/make-swish-payment";
import { MakeApplePayment } from "../../payment-methods/make-apple-payment";
import { PaymentRedirect } from "../../shared-components/payment-redirect";
import type { Translated } from "../../../../../domain/translations/translations";

export const PaymentStatusPageWithPaymentId: ReactTFC<{
  homepageLink: string;
  t: Translated<PurchaseConfirmationPageTranslation>;
  paymentId: string;
  orderId: string;
  onPaypalAdditionalData: (
    context: PaymentStepCtxReq,
  ) => (additionalData: { details: unknown; paymentData: string }) => void;
  on3DSecure2Result: (context: PaymentStepCtxReq) => (threeDSResult: string) => void;
  onApplePayPaymentAuthorised: (context: PaymentStepCtxReq) => (token: string) => void;
  onPaymentCancelled: (context: PaymentStepCtxReq) => () => void;
  onError: (context: PaymentStepCtxReq) => void;
  LoadingScreen: React.JSX.Element;
  fetchOrderPaymentStatus: (abortSignal: AbortSignal) => Promise<CustomerOrderPaymentStatusResponse>;
  contentAreaComponents: React.JSX.Element | null;
}> = props => {
  const {
    state: {
      config: { adyen },
    },
  } = useStore();

  const location = useLocation();

  const [state, setState] = useState(0);

  const redirectClickToContinue = props.t("redirect.clickToContinue");

  const swishTranslations = {
    paragraph: props.t("swish.paragraph"),
    openInApp: props.t("swish.openInApp"),
  };

  const payPalTranslations = {
    paragraph: props.t("paypal.paragraph"),
  };

  const applePayTranslations = {
    headline: "Apple Pay",
    paragraph: props.t("applepay.paragraph"),
    notAvailable: props.t("applepay.notAvailable"),
    cancelApplePayPaymentButton: props.t("applepay.cancelPaymentButton"),
  };

  const dataKey = props.paymentId + "-" + props.orderId + "-" + `${state}`;

  function onRefreshRequested() {
    setState(s => s + 1);
  }

  const fetchedPaymentOrderStatus_ = useAbortableFetched(props.fetchOrderPaymentStatus, {
    dataKey: dataKey,
  });

  const fetchedPaymentOrderStatus = useStale(fetchedPaymentOrderStatus_, 1500, t => t.__tag === "loaded");

  if (fetchedPaymentOrderStatus.__tag === "loaded") {
    const { data } = fetchedPaymentOrderStatus;

    if (data.tag === "not-found") {
      return <ErrorPage t={props.t} homepageLink={props.homepageLink} />;
    }

    const { paymentStatus, order, payment, customerFullName, canRetryPayment, contactData } = data;
    const context = {
      orderId: order.id,
      orderNumber: order.orderNumber,
      paymentId: payment.id,
    };

    const purchaseConfirmationPageSuccessScreen = (status: "confirmed" | "pending") => (
      <PurchaseConfirmationPageSuccessScreen
        context={context}
        t={props.t}
        homepageLink={props.homepageLink}
        contactData={contactData}
        paymentMethodName={payment.paymentMethodName}
        customerFullName={customerFullName}
        orderValue={order.orderValue}
        paymentStatusValue={status}
      >
        {props.contentAreaComponents}
      </PurchaseConfirmationPageSuccessScreen>
    );

    switch (paymentStatus.tag) {
      case "success": {
        return purchaseConfirmationPageSuccessScreen("confirmed");
      }

      case "pending": {
        return purchaseConfirmationPageSuccessScreen("pending");
      }

      case "error": {
        const {
          details: { message, cardHolderInfo },
        } = paymentStatus;

        return (
          <PurchaseConfirmationPageErrorScreen
            context={context}
            homepageLink={props.homepageLink}
            t={props.t}
            message={message}
            cardHolderInfo={cardHolderInfo}
            retryPayment={{
              link: {
                pathname: location.pathname,
                search: `?${new URLSearchParams({ orderId: context.orderId }).toString()}`,
              },
              buttonLabel: props.t("repeatPaymentLabel"),
            }}
            contactData={contactData}
          >
            {props.contentAreaComponents}
          </PurchaseConfirmationPageErrorScreen>
        );
      }

      case "redirect": {
        const { data } = paymentStatus;

        return (
          <Wrapper className={cn("expand fill-screen")} context={context}>
            <PaymentRedirect data={data} text={redirectClickToContinue} />
          </Wrapper>
        );
      }

      case "challenge": {
        const { data } = paymentStatus;

        return (
          <Wrapper className={cn("expand fill-screen", "align-start")} context={context}>
            <ContentContainer>
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                `}
              >
                <Payment3DSChallenge
                  action={data}
                  onError={() => props.onError(context)}
                  on3DSecureResult={props.on3DSecure2Result(context)}
                />
              </div>
            </ContentContainer>
          </Wrapper>
        );
      }

      case "paypal-button": {
        const { data: actionData, merchantId, intent, amount } = paymentStatus;

        return (
          <Wrapper className={cn("expand fill-screen")} context={context}>
            <ContentContainer>
              <PaypalPayButton
                actionData={actionData}
                onAdditionalData={props.onPaypalAdditionalData(context)}
                merchantId={merchantId}
                amount={amount}
                headline={"PayPal"}
                paragraph={payPalTranslations.paragraph}
                intent={intent}
              />
            </ContentContainer>
          </Wrapper>
        );
      }

      case "swish-qrcode": {
        const {
          data: { code, url },
        } = paymentStatus;

        return (
          <Wrapper className={cn("expand fill-screen")} context={context}>
            <ContentContainer>
              <MakeSwishPayment
                code={code}
                url={url}
                translations={swishTranslations}
                onRefreshRequested={onRefreshRequested}
              />
            </ContentContainer>
          </Wrapper>
        );
      }

      case "applepay-authorise": {
        const { data } = paymentStatus;

        return (
          <Wrapper className={cn("expand fill-screen")} context={context}>
            <ContentContainer>
              <MakeApplePayment
                data={data}
                onPaymentAuthorised={props.onApplePayPaymentAuthorised(context)}
                onPaymentCancelled={props.onPaymentCancelled(context)}
                translations={applePayTranslations}
              />
            </ContentContainer>
          </Wrapper>
        );
      }

      default: {
        const _: never = paymentStatus;

        return <ErrorPage t={props.t} homepageLink={props.homepageLink} />;
      }
    }
  } else if (fetchedPaymentOrderStatus.__tag === "loading") {
    return props.LoadingScreen;
  } else {
    return <Navigate to={props.homepageLink} />;
  }
};
